// Panneau de compatibilité 5 dimensions — partagé extension ↔ console.
//
// Volontairement SANS verdict (go / peut-être / non). Décision produit : la
// machine donne un score et des faits vérifiables, elle ne prononce pas un
// jugement à la place de l'humain. Le score et les écarts suffisent à décider,
// et ils n'ont pas l'autorité trompeuse d'un « Non » en rouge.

import type { UiLang } from '@ai-job-fit/engine/langues'
import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import type { CompatibilityRequirement, CompatibilityResult } from '@ai-job-fit/engine/scoring'
import { ScoreRing } from './ScoreRing'
import { scoreTone, type ScoreTone } from './scoreTone'

export interface ScorePanelProps {
  result: CompatibilityResult
  /** Nom affiché en tête (le consultant évalué). */
  subject?: string
  /** Sous-titre : le rôle / la spécialité, sous le nom. */
  role?: string
  /** Actions posées à droite de l'en-tête (bouton de génération, retrait…). */
  aside?: ReactNode
  labels?: ScoreLabels
  /**
   * Micro-retour 👍/👎 (« ce score vous semble-t-il juste ? »). Les deux props
   * sont optionnelles et vont ensemble : sans `onFeedback`, rien ne s'affiche.
   * Un composant monté sans backend pour le persister (test, page isolée)
   * ne montre donc pas de boutons qui ne feraient rien au clic. `feedback` est
   * l'avis déjà donné (`null` si aucun), affiché enfoncé plutôt que reposé.
   */
  feedback?: 'up' | 'down' | null
  onFeedback?: (positive: boolean) => void
  /**
   * Bandeau de 44 px (la taille du widget de l'extension) au lieu du panneau
   * héros de 88 px : un chiffre + le mot de ton lus en un regard, le détail
   * (dimensions, écarts, atouts) replié derrière un bouton plutôt qu'imposé.
   * Demandé le 25 août 2026 : le panneau plein dominait un écran où il n'est
   * plus la seule chose à montrer (offre et dossier désormais réunis).
   * `false` par défaut : les usages existants (widget, comparateur) gardent
   * leur rendu.
   */
  compact?: boolean
  /**
   * Charge les exigences citées (« Ce que l'offre demande », issue #162) à la
   * première ouverture de la section, quand le score n'en porte pas encore.
   * L'hôte fait l'appel et rend un `result` qui les contient. Absent, la
   * section n'apparaît que si le score les porte déjà.
   */
  onLoadRequirements?: () => Promise<void> | void
  /**
   * La section « Ce que l'offre demande » s'ouvre d'elle-même, et charge les
   * exigences si besoin. Radar l'active (issue #168) : c'est la réponse à
   * « qu'est-ce qu'ils veulent », qu'on ne pensait pas à déplier. `false` par
   * défaut : l'extension garde son rendu replié, qui ne coûte aucun appel.
   */
  requirementsOpen?: boolean
  /**
   * Le retour sur le score en lien nommé (« Cet avis vous semble faux ? »)
   * au lieu des deux pouces. Les pouces sont des emoji sans libellé visible :
   * on ne savait pas s'ils notaient le score ou l'offre (issue #168). Même
   * `onFeedback`, appelé avec `false` ; `merci` remplace le lien une fois
   * l'avis donné. Absent, les pouces restent (extension).
   */
  feedbackLien?: { question: string; merci: string }
  /**
   * Retire l'en-tête (anneau, palier, potentiel) ou la section des exigences.
   * Radar les montre ailleurs sur la fiche (issue #169) : l'avis en tête, les
   * exigences sous le bouton d'action, et ce panneau replié ne garde que le
   * détail (dimensions, atouts, écarts). Sans ces props, rien ne change.
   */
  masquerTete?: boolean
  masquerExigences?: boolean
}

/** Le mot affiché à côté de chaque exigence (« Couvert », « À confirmer »,
 *  « Manquant ») : la marque seule (✓ ≈ ✗) ne se lisait qu'à la couleur. */
export type MotsExigence = Record<CompatibilityRequirement['status'], string>

/**
 * « Ce que l'offre demande » (issue #162) : les phrases de l'offre, chacune
 * avec ce qui la couvre dans le profil. Sortie du panneau (issue #169) pour
 * que Radar la pose sous l'action, ouverte ; le panneau s'en sert toujours.
 * Les exigences se calculent à la première ouverture (`onLoad`), une fois.
 */
export function ExigencesCitees({
  requirements,
  onLoad,
  open = false,
  titre,
  attente,
  vide,
  mots,
  anime = false,
}: {
  requirements: CompatibilityRequirement[] | undefined
  onLoad?: () => Promise<void> | void
  open?: boolean
  titre: string
  attente: string
  vide: string
  mots?: MotsExigence
  /** Les exigences se cochent l'une après l'autre à leur première apparition
   *  (#187). L'hôte sait si c'est la première fois qu'on voit l'offre. */
  anime?: boolean
}) {
  const [chargement, setChargement] = useState<'idle' | 'busy' | 'done'>('idle')
  const ouvrir = async (ouvert: boolean) => {
    if (!ouvert || requirements || !onLoad || chargement !== 'idle') return
    setChargement('busy')
    try {
      await onLoad()
    } finally {
      setChargement('done')
    }
  }
  // Ouverte d'office : le chargement part au montage, une seule fois (le
  // garde `chargement` l'assure).
  useEffect(() => {
    if (open) void ouvrir(true)
  }, [open])
  if (!requirements && !onLoad) return null
  return (
    <details className={`reqs${anime ? ' anime' : ''}`} open={open || undefined} onToggle={(e) => void ouvrir((e.target as HTMLDetailsElement).open)} data-testid="score-requirements">
      <summary>{titre}</summary>
      {requirements?.length ? (
        <ul>
          {requirements.map((r, i) => (
            <li key={i} className={`req ${r.status}`} style={{ '--k': i } as CSSProperties}>
              <span className="req-mark" aria-hidden="true">{r.status === 'met' ? '✓' : r.status === 'missing' ? '✗' : '≈'}</span>
              <span>
                {mots && <span className="req-mot">{mots[r.status]}</span>}
                <q>{r.quote}</q>
                {r.evidence && <span className="req-ev">{r.evidence}</span>}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="req-wait">{chargement === 'done' || requirements ? vide : attente}</p>
      )}
    </details>
  )
}

export interface ScoreLabels {
  dimensions: Record<string, string>
  potential: string
  dealBreakers: string
  strengths: string
  gaps: string
  none: string
  /** Le palier écrit en toutes lettres sous l'anneau. Même règle que
   *  `ScoreRing.toneLabel` : jamais déduit dans le paquet, pour que le mot ne
   *  romprait pas si un jour ce panneau sert hors console/extension FR. */
  tone: Record<ScoreTone, string>
  feedbackUp: string
  feedbackDown: string
  /** Le bouton qui déplie le détail, en mode `compact`. Ajouté au contrat le
   *  2026-08-26 : il était écrit en dur (« Détail ») dans le JSX, donc le seul
   *  mot du panneau qu'aucun appelant ne pouvait traduire — la console est
   *  passée bilingue et il restait français au milieu d'un écran anglais. */
  detail: string
  /** Section des exigences citées (issue #162). */
  requirements: string
  requirementsLoading: string
  requirementsEmpty: string
}

/** Reprend exactement le vocabulaire déjà en place dans la pile de décisions
 *  (`Decisions.tsx`, PALIERS) : un même palier ne doit jamais avoir deux noms
 *  différents selon l'écran où on le lit. */
export const FR_SCORE_LABELS: ScoreLabels = {
  dimensions: {
    technical: 'Technique',
    experience: 'Expérience',
    seniority: 'Séniorité',
    soft: 'Soft skills',
    formal: 'Formel / langues',
  },
  potential: 'après adaptation du CV',
  dealBreakers: 'Critères éliminatoires',
  strengths: 'Atouts confirmés',
  gaps: 'Écarts à surveiller',
  none: 'aucun',
  tone: { good: 'Bonne correspondance', mid: 'À vérifier', low: 'Correspondance faible' },
  feedbackUp: 'Score pertinent',
  feedbackDown: 'Score à côté de la plaque',
  detail: 'Détail',
  requirements: 'Ce que l’offre demande',
  requirementsLoading: 'Lecture de l’offre…',
  requirementsEmpty: 'Rien à citer.',
}

/** Le même contrat en anglais. Vit ICI et pas dans une app : le panneau est
 *  partagé, et deux apps qui traduiraient chacune leur copie rouvriraient
 *  exactement la divergence que ce paquet existe pour fermer (cf. CLAUDE.md,
 *  « le même score, deux formes et deux couleurs »). Les paliers reprennent
 *  le vocabulaire de la pile de décisions anglaise (`i18n.ts`,
 *  `decisions.tierGood`…), pour la même raison qu'en français. */
export const EN_SCORE_LABELS: ScoreLabels = {
  dimensions: {
    technical: 'Technical',
    experience: 'Experience',
    seniority: 'Seniority',
    soft: 'Soft skills',
    formal: 'Formal / languages',
  },
  potential: 'after tailoring the CV',
  dealBreakers: 'Deal-breakers',
  strengths: 'Confirmed strengths',
  gaps: 'Gaps to watch',
  none: 'none',
  tone: { good: 'Good match', mid: 'To check', low: 'Weak match' },
  feedbackUp: 'Score is on point',
  feedbackDown: 'Score is way off',
  detail: 'Details',
  requirements: 'What the offer asks for',
  requirementsLoading: 'Reading the offer…',
  requirementsEmpty: 'Nothing to quote.',
}

/** Le même contrat en néerlandais (issue #137), traduction automatique pas
 *  encore relue : la langue reste cachée tant qu'elle ne l'est pas. */
export const NL_SCORE_LABELS: ScoreLabels = {
  dimensions: {
    technical: 'Technisch',
    experience: 'Ervaring',
    seniority: 'Senioriteit',
    soft: 'Soft skills',
    formal: 'Formeel / talen',
  },
  potential: 'na aanpassing van het cv',
  dealBreakers: 'Uitsluitingscriteria',
  strengths: 'Bevestigde troeven',
  gaps: 'Aandachtspunten',
  none: 'geen',
  tone: { good: 'Goede match', mid: 'Na te kijken', low: 'Zwakke match' },
  feedbackUp: 'Score klopt',
  feedbackDown: 'Score zit ernaast',
  detail: 'Details',
  requirements: 'Wat de vacature vraagt',
  requirementsLoading: 'De vacature lezen…',
  requirementsEmpty: 'Niets om te citeren.',
}

/** Le même contrat en italien (issue #137), traduction automatique pas encore relue. */
export const IT_SCORE_LABELS: ScoreLabels = {
  dimensions: {
    technical: 'Tecnica',
    experience: 'Esperienza',
    seniority: 'Anzianità',
    soft: 'Soft skill',
    formal: 'Formale / lingue',
  },
  potential: 'dopo l’adattamento del CV',
  dealBreakers: 'Criteri esclusivi',
  strengths: 'Punti di forza confermati',
  gaps: 'Lacune da tenere d’occhio',
  none: 'nessuno',
  tone: { good: 'Buona corrispondenza', mid: 'Da verificare', low: 'Corrispondenza debole' },
  feedbackUp: 'Punteggio pertinente',
  feedbackDown: 'Punteggio fuori strada',
  detail: 'Dettagli',
  requirements: 'Cosa chiede l’offerta',
  requirementsLoading: 'Lettura dell’offerta…',
  requirementsEmpty: 'Niente da citare.',
}

/** Le même contrat en espagnol (issue #137), traduction automatique pas encore relue. */
export const ES_SCORE_LABELS: ScoreLabels = {
  dimensions: {
    technical: 'Técnica',
    experience: 'Experiencia',
    seniority: 'Antigüedad',
    soft: 'Habilidades blandas',
    formal: 'Formal / idiomas',
  },
  potential: 'tras adaptar el CV',
  dealBreakers: 'Criterios excluyentes',
  strengths: 'Puntos fuertes confirmados',
  gaps: 'Carencias a vigilar',
  none: 'ninguno',
  tone: { good: 'Buena coincidencia', mid: 'Por verificar', low: 'Coincidencia débil' },
  feedbackUp: 'Puntuación acertada',
  feedbackDown: 'Puntuación desacertada',
  detail: 'Detalle',
  requirements: 'Lo que pide la oferta',
  requirementsLoading: 'Leyendo la oferta…',
  requirementsEmpty: 'Nada que citar.',
}

/** Les libellés du panneau par langue d'interface. */
export const SCORE_LABELS: Record<UiLang, ScoreLabels> = { fr: FR_SCORE_LABELS, en: EN_SCORE_LABELS, nl: NL_SCORE_LABELS, it: IT_SCORE_LABELS, es: ES_SCORE_LABELS }

export function ScorePanel({
  result,
  subject,
  role,
  aside,
  labels = FR_SCORE_LABELS,
  feedback = null,
  onFeedback,
  compact = false,
  onLoadRequirements,
  requirementsOpen = false,
  feedbackLien,
  masquerTete = false,
  masquerExigences = false,
}: ScorePanelProps) {
  const dealBreakers = result.dealBreakers ?? []
  const gaps = [...(result.majorGaps ?? []), ...(result.missingKeywords ?? []).slice(0, 5)].slice(0, 8)
  const strengths = (result.matchedKeywords ?? []).slice(0, 8)
  const tone = scoreTone(result.score)
  // Le potentiel n'a de sens que s'il DÉPASSE le score : sinon l'adaptation du
  // CV ne rapporte rien, et l'afficher promettrait un gain inexistant.
  const gain = (result.potentialScore ?? 0) - result.score
  // Replié par défaut en mode compact : c'est justement ce qui change, le
  // détail ne s'impose plus, il se demande.
  const [detailOuvert, setDetailOuvert] = useState(false)
  const detailVisible = !compact || detailOuvert
  const requirements = result.requirements

  const lienAvis = onFeedback && feedbackLien && (
    <div className="score-avis-lien">
      {feedback === 'down' ? (
        <span>{feedbackLien.merci}</span>
      ) : (
        <button type="button" className="linkish" onClick={() => onFeedback(false)}>
          {feedbackLien.question}
        </button>
      )}
    </div>
  )

  const feedbackNode = onFeedback && !feedbackLien && (
    <div className="scorefeedback" role="group" aria-label={labels.feedbackUp + ' / ' + labels.feedbackDown}>
      <button
        type="button"
        className="fb-btn"
        aria-pressed={feedback === 'up'}
        aria-label={labels.feedbackUp}
        title={labels.feedbackUp}
        onClick={() => onFeedback(true)}
      >
        👍
      </button>
      <button
        type="button"
        className="fb-btn"
        aria-pressed={feedback === 'down'}
        aria-label={labels.feedbackDown}
        title={labels.feedbackDown}
        onClick={() => onFeedback(false)}
      >
        👎
      </button>
    </div>
  )

  return (
    <div className={`scorepanel${compact ? ' compact' : ''}`}>
      {!masquerTete && <div className={`scorehead ${tone}`}>
        <ScoreRing
          score={result.score}
          potential={compact ? undefined : result.potentialScore}
          tone={tone}
          size={compact ? 44 : 88}
          toneLabel={compact ? undefined : labels.tone[tone]}
          ariaLabel={subject ? `${subject} : ${result.score} sur 100` : undefined}
        />
        <div className="scoremeta">
          {subject && <div className="scoresubject">{subject}</div>}
          {compact ? (
            <div className={`scoretone-inline ${tone}`}>{labels.tone[tone]}</div>
          ) : (
            <>
              {role && <div className="scorerole">{role}</div>}
              {gain > 0 && (
                <div className="scorepotential">
                  ↑ {result.potentialScore}/100 {labels.potential}
                </div>
              )}
            </>
          )}
        </div>
        {(aside || feedbackNode) && <div className="scorehead-aside">{feedbackNode}{aside}</div>}
        {compact && (
          <button
            type="button"
            className="detail-toggle"
            aria-expanded={detailOuvert}
            onClick={() => setDetailOuvert((v) => !v)}
          >
            {labels.detail}
          </button>
        )}
      </div>}

      {detailVisible && (
        <>
          <div className="dims">
            {(result.dimensions ?? []).map((d) => (
              <div className="dim" key={d.key}>
                <div className="dh">
                  <b>{labels.dimensions[d.key] ?? d.key}</b>
                  <span className="dv">{d.score}</span>
                </div>
                <div className="track">
                  <span className={`fill ${scoreTone(d.score)}`} style={{ width: `${d.score}%` }} />
                </div>
                {d.rationale && <div className="dr">{d.rationale}</div>}
              </div>
            ))}
          </div>

          {dealBreakers.length > 0 && (
            <div className="gapbox block">
              <div className="gh">{labels.dealBreakers}</div>
              <ul>
                {dealBreakers.map((g, i) => (
                  <li key={i}>{g}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="gaps">
            <div className="gapbox plus">
              <div className="gh">{labels.strengths}</div>
              <ul>{strengths.length ? strengths.map((k, i) => <li key={i}>{k}</li>) : <li>{labels.none}</li>}</ul>
            </div>
            <div className="gapbox minus">
              <div className="gh">{labels.gaps}</div>
              <ul>{gaps.length ? gaps.map((k, i) => <li key={i}>{k}</li>) : <li>{labels.none}</li>}</ul>
            </div>
          </div>

          {!masquerExigences && (
            <ExigencesCitees
              requirements={requirements}
              onLoad={onLoadRequirements}
              open={requirementsOpen}
              titre={labels.requirements}
              attente={labels.requirementsLoading}
              vide={labels.requirementsEmpty}
            />
          )}
          {lienAvis}
        </>
      )}
    </div>
  )
}
